import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase/firebaseConfig";
import {
  collection, getDocs, addDoc, deleteDoc, updateDoc,
  doc, query, where, orderBy, writeBatch
} from "firebase/firestore";

export default function ManageMachines() {
  const [items, setItems] = useState([]);               // [{id, number, name}]
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // single add
  const [newNumber, setNewNumber] = useState("");
  const [newName, setNewName] = useState("");

  // bulk import
  const [bulkText, setBulkText] = useState("");
  const [preview, setPreview] = useState([]);           // [{number,name,status,existingId}]
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { refresh(); }, []);

  async function refresh() {
    setLoading(true);
    try {
      // Order by 'number' as a string so 001,003,101A sort sensibly
      const snap = await getDocs(query(collection(db, "machines_catalog"), orderBy("number")));
      const rows = snap.docs
        .map(d => ({ id: d.id, ...(d.data() || {}) }))
        .filter(r => typeof r.number === "string" && r.number.trim() && typeof r.name === "string" && r.name.trim());
      setItems(rows);
    } catch (e) {
      setMsg("❌ Load error: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function addOrUpdateSingle(e) {
    e.preventDefault();
    setMsg("");
    const number = String(newNumber).trim();
    const name = String(newName).trim();
    if (!number || !name) { setMsg("❌ Enter both number and name."); return; }

    setLoading(true);
    try {
      // Upsert by number (unique by your convention)
      const snap = await getDocs(query(collection(db, "machines_catalog"), where("number", "==", number)));
      if (snap.empty) {
        await addDoc(collection(db, "machines_catalog"), { number, name });
        setMsg(`✅ Added ${number} - ${name}`);
      } else {
        // update the first match (you should only have one)
        const existing = snap.docs[0];
        await updateDoc(existing.ref, { name });
        setMsg(`✅ Updated ${number} → ${name}`);
      }
      setNewNumber(""); setNewName("");
      await refresh();
    } catch (e) {
      setMsg("❌ Save error: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function remove(id, desc) {
    if (!window.confirm(`Delete "${desc}"?`)) return;
    setLoading(true);
    setMsg("");
    try {
      await deleteDoc(doc(db, "machines_catalog", id));
      setMsg("🗑️ Deleted.");
      await refresh();
    } catch (e) {
      setMsg("❌ Delete error: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  // Parse bulk text into [{number,name}]
  function parseBulk(text) {
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const out = [];
    for (const line of lines) {
      if (/^other$/i.test(line)) continue; // skip the "OTHER" line
      // take first token as number, rest as name
      const m = line.match(/^(\S+)\s+(.+)$/);
      if (!m) continue;
      const number = m[1].trim();
      const name = m[2].trim();
      if (!number || !name) continue;
      out.push({ number, name });
    }
    return out;
  }

  function buildPreview() {
    const parsed = parseBulk(bulkText);
    if (!parsed.length) {
      setMsg("⚠️ Nothing to import (check your formatting).");
      setPreview([]);
      setShowPreview(false);
      return;
    }
    const existingByNumber = new Map(items.map(i => [i.number, i]));
    const rows = parsed.map(p => {
      const ex = existingByNumber.get(p.number);
      if (!ex) return { ...p, status: "NEW", existingId: null };
      if (ex.name === p.name) return { ...p, status: "SKIP (same)", existingId: ex.id };
      return { ...p, status: "UPDATE (name change)", existingId: ex.id };
    });
    setPreview(rows);
    setShowPreview(true);
    setMsg(`Preview ${rows.length} entries. NEW: ${rows.filter(r=>r.status.startsWith("NEW")).length}, UPDATE: ${rows.filter(r=>r.status.startsWith("UPDATE")).length}, SKIP: ${rows.filter(r=>r.status.startsWith("SKIP")).length}`);
  }

  async function applyImport() {
    if (!preview.length) return;
    setLoading(true);
    setMsg("");
    try {
      // Firestore batch (split if > 500)
      const toProcess = preview.filter(r => r.status.startsWith("NEW") || r.status.startsWith("UPDATE"));
      let idx = 0;
      while (idx < toProcess.length) {
        const slice = toProcess.slice(idx, idx + 450);
        const batch = writeBatch(db);
        for (const r of slice) {
          if (r.status.startsWith("NEW")) {
            const ref = doc(collection(db, "machines_catalog")); // random ID
            batch.set(ref, { number: r.number, name: r.name });
          } else if (r.status.startsWith("UPDATE") && r.existingId) {
            batch.update(doc(db, "machines_catalog", r.existingId), { name: r.name });
          }
        }
        await batch.commit();
        idx += slice.length;
      }
      setMsg(`✅ Import complete. Added ${preview.filter(r=>r.status.startsWith("NEW")).length}, Updated ${preview.filter(r=>r.status.startsWith("UPDATE")).length}.`);
      setShowPreview(false);
      setPreview([]);
      setBulkText("");
      await refresh();
    } catch (e) {
      setMsg("❌ Import error: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  const sortedItems = useMemo(() => {
    // Already ordered by number, but keep a stable sort
    return [...items];
  }, [items]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold mb-4">Machines Catalog Admin</h1>

      {msg && <div className="mb-4 text-sm">{msg}</div>}

      {/* Add / Update single */}
      <form onSubmit={addOrUpdateSingle} className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div className="col-span-1">
          <label className="block text-sm font-medium mb-1">Machine Number</label>
          <input
            value={newNumber}
            onChange={e => setNewNumber(e.target.value)}
            placeholder="e.g. 001 or 101A"
            className="w-full p-2 border rounded-md"
            required
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Machine Name</label>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="e.g. Bulldog Auto Guillotine"
            className="w-full p-2 border rounded-md"
            required
          />
        </div>
        <div className="col-span-1">
          <button
            type="submit"
            disabled={loading}
            className={`w-full p-2 rounded-md text-white ${loading ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {loading ? "Saving…" : "Add / Update"}
          </button>
        </div>
      </form>

      {/* Bulk import */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-1">Bulk paste (one per line: "001 Bulldog Auto Guillotine")</label>
        <textarea
          value={bulkText}
          onChange={e => setBulkText(e.target.value)}
          rows={8}
          placeholder={`001 Bulldog Auto Guillotine\n003 Edwards Guillotine\n...`}
          className="w-full p-2 border rounded-md font-mono text-sm"
        />
        <div className="mt-2 flex gap-3">
          <button
            type="button"
            onClick={buildPreview}
            disabled={loading}
            className={`px-4 py-2 rounded-md text-white ${loading ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"}`}
          >
            Preview import
          </button>
          {showPreview && (
            <button
              type="button"
              onClick={applyImport}
              disabled={loading}
              className={`px-4 py-2 rounded-md text-white ${loading ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"}`}
            >
              Apply import
            </button>
          )}
        </div>
      </div>

      {showPreview && (
        <div className="mb-6 overflow-x-auto border rounded-md">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Number</th>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((r, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-3 py-2 font-mono">{r.number}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className={`px-3 py-2 ${r.status.startsWith("NEW") ? "text-green-700" : r.status.startsWith("UPDATE") ? "text-amber-700" : "text-gray-500"}`}>
                    {r.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Current catalog */}
      <div className="overflow-x-auto border rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left">Number</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(it => {
              const desc = it.number ? `${it.number} - ${it.name}` : it.name;
              return (
                <tr key={it.id} className="border-t">
                  <td className="px-3 py-2 font-mono">{it.number}</td>
                  <td className="px-3 py-2">{it.name}</td>
                  <td className="px-3 py-2">{desc}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => remove(it.id, desc)}
                      className="px-2 py-1 text-white bg-red-600 hover:bg-red-700 rounded-md"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
            {!items.length && !loading && (
              <tr><td className="px-3 py-3 text-gray-500" colSpan={4}>No machines in catalog.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
